/** @jsxImportSource @emotion/react */
import { useEffect, useState, useRef } from "react";
import ProjectsHeader from "../components/MainComponents/Projects/ProjectsHeader";
import NoProjectsCard from "../components/MainComponents/Projects/NoProjectsCard";
import {
  deleteProject,
  listProjects,
  patchProject,
  ProjectsModel,
} from "../services/projectsService";
import ProjectsTable from "../components/MainComponents/Projects/ProjectsTable";
import Loader from "../components/MainComponents/Loader/Loader";
import DeleteProjectDialog from "../components/MainComponents/Projects/DeleteProjectDialog";
import { useToast } from "@coreai/component-library";
import { useLanguage } from "LanguageContext";
import { sortingColumnsProps } from "../components/MainComponents/DataTable/DataTable";

export default function ProjectsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [newProject, setNewProject] = useState<boolean>(false);
  const [showAlertDialog, setShowAlertDialog] = useState(false);

  const [projects, setProjects] = useState<ProjectsModel[]>([]);
  const [projectData, setProjectData] = useState<any>();
  const [projectIdToDelete, setProjectIdToDelete] = useState("");
  const [isTableDataLoading, setIsTableDataLoading] = useState(false); 
  const [sortingColumns, setSortingColumns] = useState<sortingColumnsProps>({});
  const prevSorting = useRef<object | null>(null);  
  const [pageNo, setPageNo] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { toast } = useToast();
  const { t } = useLanguage();
  const isEmptyObject = (obj: unknown): boolean => {
    return obj !== null && typeof obj === "object" && Object.keys(obj).length === 0;
  };
  
  const fetchProjects = () => {
    setIsLoading(true);
    listProjects({sortingColumns})
      .then((projectsList) => {
        setProjectData(projectsList);
        setProjects(
          projectsList.results.map((result: any) => ({
            advertiser: result.advertiser,
            id: result.id,
            name: result.name,
            total_analysis: result.total_analysis,
          }))
        );
      }).catch((error) => {
        console.error("Error fetching projects:", error);
      }).finally(() => setIsLoading(false));
  };

    useEffect(() => {
       // Only update if sortingColumns has actually changed
    if(isEmptyObject(sortingColumns)){
      return;
    }
    if (JSON.stringify(prevSorting.current) === JSON.stringify(sortingColumns)) {
      return;
    }  
      setIsTableDataLoading(true);
      listProjects({sortingColumns})
      .then((projectsList) => {
        setProjectData(projectsList);
        setProjects(
          projectsList.results.map((result: any) => ({
            advertiser: result.advertiser,
            id: result.id,
            name: result.name,
            total_analysis: result.total_analysis,
          }))
        );
        prevSorting.current = sortingColumns;
      }).catch((error) => {
        console.error("Error fetching projects:", error);
      }).finally(() => setIsTableDataLoading(false));
    }, [sortingColumns]);

  useEffect(() => {
    if (!newProject) {
      fetchProjects();
    }
  }, [newProject]);

  const onDelete = (projectId: string) => {
    setIsLoading(true);
      deleteProject(projectId).then(()=>{
        toast({
        variant: 'success',
        title: t("project_toast_delete_success"),
        });
        fetchProjects();
      }).catch((error)=>{
        console.error("Error deleting project:", error);  
      }).finally(()=>{
        setIsLoading(false);  
      })     
    
  };

  const onEdit = (projectId: string, updatedProject: any) => {
    setIsLoading(true);
      patchProject(projectId, updatedProject).then(()=>{
        toast({
        variant: 'success',
        title: t("project_toast_edit_success"),
        });
        fetchProjects();
       }).catch((error)=>{
           setIsLoading(false);
          if (error?.status === 400) {
            toast({
              variant: 'error',
              title: t("error_title"),
              description: t("save_project_toast_error_message", {
                projectName: updatedProject.name,
                advertiserName: updatedProject.advertiser,
              }),
            });
          }
          console.error("Error editing project:", error);
       }).finally(()=>{
        setIsLoading(false);
       })

  };

  const onCloseDialog = () => {
    setShowAlertDialog(false);
  };

  const onDeleteOpenDialog = (projectId: string) => {
    setShowAlertDialog(true);
    setProjectIdToDelete(projectId);
  };

  const onPageChange = (page: number) => {
    setPageNo(page);
  };

  let contentToRender;
  if (isLoading) {
    contentToRender = <Loader />;
  } else if (projects.length === 0 && !isLoading) {
    contentToRender = <NoProjectsCard setNewProject={setNewProject} />;
  } else {
    contentToRender = (
      <ProjectsTable
        projectData={projectData}
        onEdit={onEdit}
        onDelete={onDeleteOpenDialog}
        sortingColumns={{...sortingColumns}}
        setSortingColumns={setSortingColumns}
        isTableDataLoading={isTableDataLoading}
        setPageNo={onPageChange}
        pageNo={pageNo}
        setRowsPerPage={setRowsPerPage}
        rowsPerPage={rowsPerPage}
      />
    );
  }  
  return (
    <>
      <div>
        <ProjectsHeader setNewProject={setNewProject} totalProjects={projects.length} />
        {contentToRender}
      </div>
      <DeleteProjectDialog
        project={projects.find((project) => project.id === projectIdToDelete)}
        open={showAlertDialog}
        onClose={onCloseDialog}
        onDelete={onDelete}
      />
    </>
  );
}
